import React, { useMemo } from 'react';

import Icon from 'carbon-react/lib/components/icon';

import { ExtendedLiveReportList } from '@futrli/views/processors/dashboards/dashboardsList';
import { FormattedMessage } from '@futrli/localisation';
import { ListingPageRowActionItem } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-actions/listing-page-row-actions';

import { useDashboardActions } from '../context/dashboard-actions-context/dashboard-actions-context';

interface UseDashboardActionItemsParams {
  dashboard: ExtendedLiveReportList;
}

export const useDashboardActionItems = ({ dashboard }: UseDashboardActionItemsParams): ListingPageRowActionItem[] => {
  const {
    handleAddToPerformanceAreaClick,
    handleAddToTemplatesClick,
    handleDeleteClick,
    handleDuplicateClick,
    handleEditClick,
    handlePublishClick,
    handleRemoveFromPerformanceAreaClick,
    handleShareClick,
    handleUnpublishClick
  } = useDashboardActions();

  return useMemo<ListingPageRowActionItem[]>(() => {
    const items: ListingPageRowActionItem[] = [];
    const { actions } = dashboard;

    if (actions.edit) {
      items.push({
        label: <FormattedMessage id="common.edit" defaultMessage="Edit" />,
        onClick: () => {
          handleEditClick(dashboard);
        }
      });
    }

    if (actions.duplicate) {
      items.push({
        label: <FormattedMessage id="common.duplicate" defaultMessage="Duplicate" />,
        onClick: () => {
          handleDuplicateClick(dashboard);
        }
      });
    }

    if (actions.publish) {
      items.push({
        label: <FormattedMessage id="common.publish" defaultMessage="Publish" />,
        onClick: () => {
          handlePublishClick(dashboard);
        }
      });
    }

    if (actions.share) {
      items.push({
        label: <FormattedMessage id="common.manage_access" defaultMessage="Manage access" />,
        onClick: () => {
          handleShareClick(dashboard);
        }
      });
    }

    if (actions.unpublish) {
      items.push({
        label: <FormattedMessage id="common.unpublish" defaultMessage="Unpublish" />,
        onClick: () => {
          handleUnpublishClick(dashboard);
        }
      });
    }

    if (actions.addToPerformanceArea) {
      items.push({
        label: (
          <FormattedMessage
            id="predict.dashboards.actions.add_to_performance_area"
            defaultMessage="Add to performance area"
          />
        ),
        onClick: () => {
          handleAddToPerformanceAreaClick(dashboard);
        }
      });
    }

    if (actions.removeFromPerformanceArea) {
      items.push({
        label: (
          <FormattedMessage
            id="predict.dashboards.actions.remove_from_performance_area"
            defaultMessage="Remove from performance area"
          />
        ),
        onClick: () => {
          handleRemoveFromPerformanceAreaClick(dashboard);
        }
      });
    }

    if (actions.addToTemplates) {
      items.push({
        label: <FormattedMessage id="predict.dashboards.actions.add_to_templates" defaultMessage="Add to templates" />,
        onClick: () => {
          handleAddToTemplatesClick(dashboard);
        }
      });
    }

    if (actions.delete) {
      items.push({
        label: (
          <>
            <Icon type="delete" fontSize="small" />
            <FormattedMessage id="common.delete" defaultMessage="Delete" />
          </>
        ),
        onClick: () => {
          handleDeleteClick(dashboard);
        },
        variant: 'danger'
      });
    }

    return items;
  }, [
    dashboard,
    handleAddToPerformanceAreaClick,
    handleAddToTemplatesClick,
    handleDeleteClick,
    handleDuplicateClick,
    handleEditClick,
    handlePublishClick,
    handleRemoveFromPerformanceAreaClick,
    handleShareClick,
    handleUnpublishClick
  ]);
};
